import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Download, Loader2, Search, X } from 'lucide-react'

import { api } from '@/api/client'
import { inputCls } from '@/components/Field'
import { PurchasePartnerCombo } from '@/components/PurchasePartnerCombo'
import { VIZ, WeeklyBars } from '@/components/charts/WeeklyBars'
import { cn } from '@/lib/utils'
import { useDebouncedValue } from '@/lib/useListPerf'
import { useFileDownload } from '@/lib/useFileDownload'
import { useToast } from '@/stores/toast'

interface ItemHit { id: number; code: string; name: string; unit_out: string; spec: string }
interface Week {
  start: string; end: string; from: string; to: string; days: number
  qty: number; amount: number; count: number
  avg_price: number | null; std: number | null; min_price: number | null; max_price: number | null
  by_item: Record<string, number>
}
interface Group {
  unit: string
  items: { id: number; code: string; name: string; unit_out: string; qty: number }[]
  weeks: Week[]
  summary: {
    qty: number; amount: number; count: number; avg_price: number | null; std: number | null
    min_price: number | null; max_price: number | null
    total_qty: number; week_avg_qty: number; max_week: { start: string; qty: number } | null; cv: number | null
  }
  recommend: { n_weeks: number; k: number; mean: number | null; std: number; qty: number | null }
  top_partners: { partner_id: number; name: string; qty: number; share: number }[]
  last_year: { from: string; to: string; weeks: number[]; has_data: boolean; loaded: boolean }
  conversions: { item_id: number; name: string; from_unit: string; kg_per_unit: number }[]   // kg 환산한 품목
  weekday: { dow: number; qty: number; days: number; avg: number }[]   // 월(0)~일(6)
}
interface Resp { from: string; to: string; groups: Group[] }

const TOP_SERIES = 5        // 품목별 그래프에 색으로 그리는 품목 수(나머지는 '기타')
const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
const addDays = (s: string, n: number) => { const d = new Date(s + 'T00:00:00'); d.setDate(d.getDate() + n); return iso(d) }
const n0 = (v: number | null | undefined) => (v === null || v === undefined ? '-' : Math.round(v).toLocaleString('ko-KR'))
const q1 = (v: number | null | undefined) => (v === null || v === undefined ? '-' : v.toLocaleString('ko-KR', { maximumFractionDigits: 1 }))
const md = (s: string) => `${Number(s.slice(5, 7))}/${Number(s.slice(8, 10))}`
const DOW = ['월', '화', '수', '목', '금', '토', '일']

/** 회사관리 > 분석 — 품목별 판매량·단가(출고 기준, 출고 단위별). 관리자·지정 인원만. */
export function AnalysisTab() {
  const push = useToast((s) => s.push)
  const today = iso(new Date())
  const [q, setQ] = useState('')
  const dq = useDebouncedValue(q.trim())
  const [picked, setPicked] = useState<ItemHit[]>([])
  const [from, setFrom] = useState(addDays(today, -90))
  const [to, setTo] = useState(today)
  const [scope, setScope] = useState<{ kind: 'group' | 'partner'; id: number; label: string } | null>(null)
  const [view, setView] = useState<'sum' | 'each'>('sum')
  const [nWeeks, setNWeeks] = useState(4)
  const [kFactor, setK] = useState(1.0)
  const [showLY, setShowLY] = useState(false)      // 켜면 전년 동기 포함해 다시 조회
  const [convertKg, setConvertKg] = useState(true) // 품명 무게 표기 품목 kg 환산
  const [run, setRun] = useState<string | null>(null)       // 조회 버튼을 누른 시점의 조건
  const { downloading, download } = useFileDownload()

  const { data: hits = [], isFetching: searching } = useQuery({
    queryKey: ['analysis-items', dq],
    queryFn: () => api<ItemHit[]>(`/api/analysis/items?q=${encodeURIComponent(dq)}`),
    enabled: !!dq,
    placeholderData: keepPreviousData,
  })
  const opts = `&kg=${convertKg ? 1 : 0}${showLY ? '&ly=1' : ''}`
  const { data, isFetching, error } = useQuery({
    queryKey: ['analysis-sales', run, convertKg, showLY],
    queryFn: () => api<Resp>(`/api/analysis/sales?${run}${opts}`),
    enabled: !!run,
    placeholderData: keepPreviousData,
  })

  const pickedIds = new Set(picked.map((p) => p.id))
  function toggle(it: ItemHit) {
    if (pickedIds.has(it.id)) { setPicked(picked.filter((p) => p.id !== it.id)); return }
    setPicked([...picked, it])
  }
  function selectAllHits() {
    const add = hits.filter((h) => !pickedIds.has(h.id))
    setPicked([...picked, ...add])
  }
  function clearHits() { setPicked(picked.filter((p) => !hits.some((h) => h.id === p.id))) }
  function preset(kind: string) {
    const t = new Date()
    if (kind === 'this') { setFrom(iso(new Date(t.getFullYear(), t.getMonth(), 1))); setTo(today) }
    else if (kind === 'last') { setFrom(iso(new Date(t.getFullYear(), t.getMonth() - 1, 1))); setTo(iso(new Date(t.getFullYear(), t.getMonth(), 0))) }
    else if (kind === 'quarter') { const qs = Math.floor(t.getMonth() / 3) * 3 - 3; setFrom(iso(new Date(t.getFullYear(), qs, 1))); setTo(iso(new Date(t.getFullYear(), qs + 3, 0))) }
    else if (kind === '6m') { setTo(today); setFrom(addDays(today, -182)) }
    else if (kind === '1y') { setTo(today); setFrom(addDays(today, -364)) }
    else if (kind === '2y') { setTo(today); setFrom(addDays(today, -728)) }
    else { const w = Number(kind); setTo(today); setFrom(addDays(today, -(w * 7) + 1)) }
  }
  const params = useMemo(() => {
    const p = new URLSearchParams({ items: picked.map((x) => x.id).join(','), from, to, n: String(nWeeks), k: String(kFactor) })
    if (scope) p.set(scope.kind, String(scope.id))
    return p.toString()
  }, [picked, from, to, scope, nWeeks, kFactor])
  function doRun() {
    if (!picked.length) { push('품목을 선택하세요.', 'warn'); return }
    if (from > to) { push('시작일이 종료일보다 늦습니다.', 'warn'); return }
    setRun(params)
  }

  return (
    <div className="space-y-4">
      {/* 조회 조건 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-sm">
        <div className="flex items-start gap-3 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 w-14 pt-2">품목</label>
          <div className="flex-1 min-w-[280px]">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5"/>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="품목명·코드 검색 (예: 루꼴라)"
                     className={inputCls + ' pl-8'}/>
            </div>
            {!!dq && (
              <div className="mt-1 border border-slate-200 rounded-lg max-h-48 overflow-auto">
                <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 border-b border-slate-100 text-xs text-slate-500 sticky top-0">
                  {searching ? '검색 중…' : `검색 ${hits.length}건`}
                  <button onClick={selectAllHits} className="ml-auto text-blue-600 hover:underline">전체선택</button>
                  <button onClick={clearHits} className="text-blue-600 hover:underline">해제</button>
                </div>
                {hits.map((h) => (
                  <label key={h.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={pickedIds.has(h.id)} onChange={() => toggle(h)}/>
                    <span className="font-mono text-xs text-slate-400">{h.code}</span>
                    <span>{h.name}</span>
                    <span className="text-xs text-slate-500 bg-slate-100 rounded px-1.5">{h.unit_out}</span>
                  </label>
                ))}
                {!searching && !hits.length && <div className="px-2 py-2 text-xs text-slate-400">검색 결과 없음</div>}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {picked.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-2 pr-1 py-0.5">
                  {p.name} <span className="text-blue-500">({p.unit_out})</span>
                  <button onClick={() => toggle(p)} className="hover:bg-blue-100 rounded-full p-0.5" title="제외"><X className="w-3 h-3"/></button>
                </span>
              ))}
              {!picked.length && <span className="text-xs text-slate-400">검색 후 체크해서 추가 (다시 검색해도 선택 유지 · 조회량이 너무 많으면 기간을 줄여 주세요)</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 w-14">기간</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls + ' !w-40 tabular-nums'}/>
          <span className="text-slate-400">~</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls + ' !w-40 tabular-nums'}/>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs">
            {([['4', '4주'], ['8', '8주'], ['13', '13주'], ['this', '이번 달'], ['last', '지난 달'], ['quarter', '지난 분기'], ['6m', '6개월'], ['1y', '1년'], ['2y', '2년']] as const).map(([k, l]) => (
              <button key={k} onClick={() => preset(k)} className="px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-600 border-r last:border-r-0 border-slate-200">{l}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 w-14">거래처</label>
          <div className="w-72">
            <PurchasePartnerCombo withGroups label={scope?.label ?? ''} placeholder="전체 업장 (검색해서 지정)"
              onChange={(h) => setScope({ kind: h.is_group ? 'group' : 'partner', id: h.id, label: `${h.is_group ? '[상위] ' : ''}${h.name}` })}/>
          </div>
          {scope && <button onClick={() => setScope(null)} className="text-xs text-slate-500 border border-slate-300 rounded-lg px-2 py-1.5">전체로</button>}
          <span className="text-xs font-semibold text-slate-500 ml-2">보기</span>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs">
            {([['sum', '합산'], ['each', '품목별']] as const).map(([k, l]) => (
              <button key={k} onClick={() => setView(k)}
                      className={cn('px-3 py-1.5 font-semibold', view === k ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')}>{l}</button>
            ))}
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 ml-2"
                 title="품명에 무게가 적힌 품목(예: 루꼴라(500g), 바나나(13kg/box))은 kg 으로 환산해 합칩니다. 무게 표기 없는 box·개 등은 원래 단위로 따로.">
            <input type="checkbox" checked={convertKg} onChange={(e) => setConvertKg(e.target.checked)}/> kg 환산
          </label>
          <span className="text-xs font-semibold text-slate-500 ml-2" title="권장 발주량 = 최근 N주 평균 + k × 표준편차">권장량</span>
          <label className="text-xs text-slate-500 inline-flex items-center">최근<input type="number" min={1} max={13} value={nWeeks}
                 onChange={(e) => setNWeeks(Math.max(1, Math.min(13, Number(e.target.value) || 4)))}
                 className={inputCls + ' !w-14 !py-1 mx-1 text-center'}/>주</label>
          <label className="text-xs text-slate-500 inline-flex items-center">k<input type="number" min={0} max={3} step={0.5} value={kFactor}
                 onChange={(e) => setK(Math.max(0, Math.min(3, Number(e.target.value) || 0)))}
                 className={inputCls + ' !w-16 !py-1 ml-1 text-center'}/></label>
          <button onClick={doRun} disabled={isFetching}
                  className="ml-auto bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg px-5 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
            {isFetching && <Loader2 className="w-4 h-4 animate-spin"/>}조회
          </button>
          <button onClick={() => run && download(`/api/analysis/sales/export?${run}${opts}`, `판매분석_${from}_${to}.xlsx`)}
                  disabled={!data || downloading}
                  className="border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-40">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}엑셀
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-rose-600">{(error as Error).message}</div>}
      {!data && !isFetching && (
        <div className="text-center py-16 text-slate-400 text-sm bg-white rounded-xl border border-slate-200">
          품목을 선택하고 조회하세요. 출고(판매) 기준입니다. kg 환산을 켜면 품명에 무게가 적힌 팩·박스는 kg 으로 합치고,
          무게 표기가 없는 품목(개·단 등)은 원래 단위로 따로 보여줍니다.
        </div>
      )}
      {data?.groups.map((g) => (
        <UnitSection key={g.unit} g={g} view={view} showLY={showLY} setShowLY={setShowLY}/>
      ))}
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  )
}

function UnitSection({ g, view, showLY, setShowLY }: {
  g: Group; view: 'sum' | 'each'; showLY: boolean; setShowLY: (v: boolean) => void
}) {
  const u = g.unit
  const s = g.summary
  const labels = g.weeks.map((w) => md(w.start) + (w.days < 7 ? '*' : ''))
  const qty = g.weeks.map((w) => w.qty)
  const maxIdx = s.max_week ? g.weeks.findIndex((w) => w.start === s.max_week!.start) : null
  const each = view === 'each' && g.items.length > 1
  // 품목별: 판매량 상위 TOP_SERIES 개만 색으로, 나머지는 '기타'로 합침(색 구분 한계). 표·엑셀은 전부.
  const ranked = [...g.items].sort((a, b) => b.qty - a.qty)
  const topItems = ranked.slice(0, TOP_SERIES)
  const others = ranked.slice(TOP_SERIES)
  const otherOf = (w: Week) => others.reduce((t, it) => t + (w.by_item[String(it.id)] ?? 0), 0)
  const cols = [
    ...topItems.map((it, i) => ({ key: String(it.id), name: it.name, color: VIZ.cat[i % VIZ.cat.length],
                                  val: (w: Week) => w.by_item[String(it.id)] ?? 0 })),
    ...(others.length ? [{ key: 'other', name: `기타 ${others.length}개`, color: VIZ.muted, val: otherOf }] : []),
  ]
  const series = each
    ? cols.map((c) => ({ key: c.key, name: c.name, color: c.color, values: g.weeks.map(c.val) }))
    : [{ key: 'sum', name: g.items.map((i) => i.name).join(' + '), color: VIZ.accent, values: qty }]
  const title = g.items.length > 3
    ? `${g.items.slice(0, 3).map((i) => i.name).join(' + ')} 외 ${g.items.length - 3}개`
    : g.items.map((i) => i.name).join(' + ')
  const pct = s.max_week && s.week_avg_qty ? Math.round((s.max_week.qty / s.week_avg_qty - 1) * 100) : null

  const qtyTip = (i: number) => {
    const w = g.weeks[i]
    return (
      <div className="space-y-0.5">
        <div className="font-semibold">{w.from} ~ {w.to}{w.days < 7 ? ` (${w.days}일)` : ''}</div>
        {each
          ? cols.map((c) => (
            <div key={c.key} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: c.color }}/>
              {c.name}: <b>{q1(c.val(w))} {u}</b>
            </div>))
          : <div>판매량 <b>{q1(w.qty)} {u}</b></div>}
        {showLY && g.last_year.has_data && <div className="text-slate-500">전년 동기 {q1(g.last_year.weeks[i])} {u}</div>}
        <div className="text-slate-500">매출 {n0(w.amount)}원 · {w.count}건</div>
      </div>
    )
  }
  const priceTip = (i: number) => {
    const w = g.weeks[i]
    return (
      <div className="space-y-0.5">
        <div className="font-semibold">{w.from} ~ {w.to}{w.days < 7 ? ` (${w.days}일)` : ''}</div>
        <div>평균 <b>{n0(w.avg_price)}원/{u}</b></div>
        <div>표준편차 {w.std === null ? '-' : `±${n0(w.std)}`}</div>
        <div>최저 {n0(w.min_price)} · 최고 {n0(w.max_price)}</div>
        <div className="text-slate-500">거래 {w.count}건</div>
      </div>
    )
  }

  if (!s.total_qty) {   // 기간 내 판매 없는 단위 — 빈 그래프 대신 한 줄로
    return (
      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-500">
        <b className="text-slate-800">{title}</b> <span className="text-xs bg-slate-100 rounded px-1.5 py-0.5 mx-1">단위 {u}</span>
        — 이 기간({md(g.weeks[0].from)}~{md(g.weeks[g.weeks.length - 1].to)})에 판매가 없습니다.
      </div>
    )
  }
  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2 pt-2">
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">단위 {u}</span>
      </div>
      {!!g.conversions?.length && (
        <div className="text-xs text-slate-500 -mt-2"
             title={g.conversions.map((c) => `${c.name} 1${c.from_unit} = ${c.kg_per_unit}kg`).join('\n')}>
          kg 환산(품명 무게 기준): {g.conversions.slice(0, 6).map((c) => `${c.name} 1${c.from_unit}=${c.kg_per_unit}kg`).join(' · ')}
          {g.conversions.length > 6 && ` 외 ${g.conversions.length - 6}개`}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="기간 총 판매량" value={`${q1(s.total_qty)} ${u}`} sub={`주 평균 ${q1(s.week_avg_qty)} ${u}`}/>
        <Stat label="최대 판매 주" value={s.max_week ? `${q1(s.max_week.qty)} ${u}` : '-'}
              sub={s.max_week ? `${md(s.max_week.start)} 주${pct !== null ? ` · 평균보다 ${pct}% 많음` : ''}` : undefined}/>
        <Stat label={`가중평균 단가 (원/${u})`} value={n0(s.avg_price)}
              sub={`최저 ${n0(s.min_price)} · 최고 ${n0(s.max_price)} · 표준편차 ${s.std === null ? '-' : n0(s.std)}`}/>
        <Stat label="총 매출액" value={`${n0(s.amount)}원`} sub={`거래 ${s.count.toLocaleString()}건`}/>
        <Stat label="권장 발주량 (다음 주)" value={g.recommend.qty === null ? '-' : `${q1(g.recommend.qty)} ${u}`}
              sub={g.recommend.mean === null ? '온전한 주 없음' : `최근 ${g.recommend.n_weeks}주 평균 ${q1(g.recommend.mean)} + ${g.recommend.k} × ${q1(g.recommend.std)}`}/>
        <Stat label="수요 안정성 (변동계수)" value={s.cv === null ? '-' : s.cv.toFixed(2)}
              sub={s.cv === null ? undefined : s.cv < 0.2 ? '매주 비슷하게 팔림' : s.cv < 0.5 ? '보통' : '주마다 변동 큼 — 여유 물량 권장'}/>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center mb-2">
          <div>
            <div className="text-sm font-semibold text-slate-800">주간 판매량 ({u}){each ? ' — 품목별' : ' — 최대 주 강조'}</div>
            <div className="text-xs text-slate-500">월~일 기준{labels.some((l) => l.endsWith('*')) ? ' · * = 기간에 일부만 포함된 주' : ''}{!each ? ' · 점선 = 기간 평균' : ''}</div>
          </div>
          <label className="ml-auto text-xs inline-flex items-center gap-1.5 text-slate-600"
                 title={`${g.last_year.from} ~ ${g.last_year.to} (켜면 다시 조회)`}>
            <input type="checkbox" checked={showLY} onChange={(e) => setShowLY(e.target.checked)}/>
            전년 동기 겹쳐 보기
            {showLY && g.last_year.loaded && !g.last_year.has_data && <span className="text-slate-400">(전년 같은 기간 판매 없음)</span>}
          </label>
        </div>
        <WeeklyBars labels={labels} series={series} highlight={each ? null : maxIdx}
                    refLine={each ? null : { value: s.week_avg_qty, label: `기간 평균 ${q1(s.week_avg_qty)}${u}` }}
                    overlay={showLY && g.last_year.has_data ? { name: '전년 동기', values: g.last_year.weeks } : null}
                    tooltip={qtyTip} ariaLabel={`${title} 주간 판매량`}/>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-800">주간 평균단가 (원/{u}) ± 표준편차</div>
        <div className="text-xs text-slate-500 mb-2">막대 = 그 주 가중평균 단가, 세로선 = ±1 표준편차(거래마다 단가 차이가 클수록 김) · 강조 = 판매량 최대 주{each ? ' · 단가는 단위 합산 기준' : ''}</div>
        <WeeklyBars labels={labels}
                    series={[{ key: 'price', name: '평균단가', color: VIZ.accent, values: g.weeks.map((w) => w.avg_price ?? 0) }]}
                    highlight={maxIdx}
                    errors={g.weeks.map((w) => (w.avg_price !== null && w.std !== null ? { lo: w.avg_price - w.std, hi: w.avg_price + w.std } : null))}
                    tooltip={priceTip} ariaLabel={`${title} 주간 평균단가`}/>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-800">요일별 하루 평균 판매량 ({u})</div>
        <div className="text-xs text-slate-500 mb-2">그 요일 판매량 합 ÷ 기간 안 그 요일 날 수(판매 없는 날 포함) · 강조 = 가장 많이 팔린 요일</div>
        {(() => {
          const wd = g.weekday ?? []
          const best = wd.length && wd.some((x) => x.avg > 0) ? wd.reduce((a, b) => (b.avg > a.avg ? b : a)).dow : null
          return (
            <WeeklyBars labels={wd.map((x) => DOW[x.dow])} height={180}
                        series={[{ key: 'dow', name: '하루 평균', color: VIZ.accent, values: wd.map((x) => x.avg) }]}
                        highlight={best}
                        tooltip={(i) => (
                          <div className="space-y-0.5">
                            <div className="font-semibold">{DOW[wd[i].dow]}요일</div>
                            <div>하루 평균 <b>{q1(wd[i].avg)} {u}</b></div>
                            <div className="text-slate-500">합계 {q1(wd[i].qty)} {u} · 기간 내 {wd[i].days}일</div>
                          </div>
                        )}
                        ariaLabel={`${title} 요일별 하루 평균 판매량`}/>
          )
        })()}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 overflow-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">주차</th>
                <th className="px-3 py-2 text-right font-medium">판매량({u})</th>
                {each && cols.map((c) => <th key={c.key} className="px-3 py-2 text-right font-medium">{c.name}</th>)}
                <th className="px-3 py-2 text-right font-medium">매출액</th>
                <th className="px-3 py-2 text-right font-medium">평균단가</th>
                <th className="px-3 py-2 text-right font-medium">표준편차</th>
                <th className="px-3 py-2 text-right font-medium">최저·최고</th>
                <th className="px-3 py-2 text-right font-medium">건수</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {g.weeks.map((w, i) => (
                <tr key={w.start} className={cn('border-t border-slate-100', i === maxIdx && 'bg-blue-50/60')}>
                  <td className="px-3 py-1.5">{md(w.from)}~{md(w.to)}{w.days < 7 && <span className="text-xs text-slate-400"> ({w.days}일)</span>}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{q1(w.qty)}</td>
                  {each && cols.map((c) => <td key={c.key} className="px-3 py-1.5 text-right text-slate-600">{q1(c.val(w))}</td>)}
                  <td className="px-3 py-1.5 text-right">{n0(w.amount)}</td>
                  <td className="px-3 py-1.5 text-right">{n0(w.avg_price)}</td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{w.std === null ? '-' : n0(w.std)}</td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{n0(w.min_price)}·{n0(w.max_price)}</td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{w.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-800 mb-2">판매 상위 10개 업장</div>
          {!g.top_partners.length && <div className="text-xs text-slate-400">판매 없음</div>}
          <ol className="space-y-1.5">
            {g.top_partners.map((t, i) => (
              <li key={t.partner_id} className="text-xs">
                <div className="flex gap-1.5">
                  <span className="text-slate-400 w-4 text-right">{i + 1}</span>
                  <span className="text-slate-800 truncate flex-1" title={t.name}>{t.name}</span>
                  <span className="tabular-nums text-slate-700">{q1(t.qty)}{u}</span>
                  <span className="tabular-nums text-slate-500 w-10 text-right">{Math.round(t.share * 100)}%</span>
                </div>
                <div className="ml-5 mt-0.5 h-1.5 rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full" style={{ width: `${Math.max(2, t.share * 100)}%`, background: VIZ.accent }}/>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
